import React, { useEffect, useMemo, useState } from "react";
import { FilterCheckboxListProps } from "./FilterCheckboxList.types";
import {
  ButtonWrapper,
  Checkbox,
  EmptyOptions,
  FilterContainer,
  FilterCount,
  FilterHeader,
  FilterTitle,
  OptionCount,
  OptionItem,
  OptionLabel,
  OptionWrapper,
  ResetButton,
  SaveButton,
  SearchInput,
  SearchWrapper,
} from "./FilterCheckboxList.styles";

const SEARCH_THRESHOLD = 6;

const FilterCheckboxList: React.FC<FilterCheckboxListProps> = ({
  options,
  onSave,
  onReset,
  selectedOptions,
  title = "Роль",
  searchPlaceholder = "Найти роль",
}) => {
  const [selected, setSelected] = useState<Set<string>>(
    new Set(selectedOptions || []),
  );
  const [query, setQuery] = useState("");

  useEffect(() => {
    setSelected(new Set(selectedOptions || []));
  }, [selectedOptions]);

  const visibleOptions = useMemo(() => {
    const normalizedQuery = query.trim().toLocaleLowerCase("ru-RU");
    if (!normalizedQuery) {
      return options;
    }

    return options.filter((option) =>
      option.label.toLocaleLowerCase("ru-RU").includes(normalizedQuery),
    );
  }, [options, query]);

  const handleToggle = (option: string) => {
    setSelected((previous) => {
      const next = new Set(previous);
      if (next.has(option)) {
        next.delete(option);
      } else {
        next.add(option);
      }
      return next;
    });
  };

  const handleReset = () => {
    setSelected(new Set());
    setQuery("");
    onReset();
  };

  const dialogLabel = title === "Роль" ? "Фильтр по роли" : `Фильтр: ${title}`;
  const searchLabel = title === "Роль" ? "Поиск по ролям" : `Поиск: ${title}`;

  return (
    <FilterContainer role="dialog" aria-label={dialogLabel}>
      <FilterHeader>
        <FilterTitle>{title}</FilterTitle>
        <FilterCount>{selected.size ? `выбрано ${selected.size}` : ""}</FilterCount>
      </FilterHeader>

      {options.length > SEARCH_THRESHOLD && (
        <SearchWrapper>
          <SearchInput
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={searchPlaceholder}
            aria-label={searchLabel}
          />
        </SearchWrapper>
      )}

      <OptionWrapper>
        {visibleOptions.length ? (
          visibleOptions.map((option) => (
            <OptionItem key={option.value}>
              <Checkbox
                type="checkbox"
                checked={selected.has(option.value)}
                onChange={() => handleToggle(option.value)}
              />
              <OptionLabel title={option.label}>{option.label}</OptionLabel>
              {typeof option.count === "number" && (
                <OptionCount>{option.count}</OptionCount>
              )}
            </OptionItem>
          ))
        ) : (
          <EmptyOptions>Ничего не найдено</EmptyOptions>
        )}
      </OptionWrapper>

      <ButtonWrapper>
        <SaveButton type="button" onClick={() => onSave(Array.from(selected))}>
          Применить
        </SaveButton>
        <ResetButton
          type="button"
          onClick={handleReset}
          disabled={selected.size === 0 && (selectedOptions?.length ?? 0) === 0}
        >
          Сбросить
        </ResetButton>
      </ButtonWrapper>
    </FilterContainer>
  );
};

export default FilterCheckboxList;
